import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
type UseCommentEditorOptions = {
  onModEnter?: () => void;
  onEscape?: () => void;
};
type SetRangeTextOptions = {
  start?: number;
  end?: number;
  selectionMode?: SelectionMode;
};
type Snapshot = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
};
const MAX_UNDO_STACK_SIZE = 100;
const MARKER_MAP = {
  bold: '**',
  italic: '_',
  strikethrough: '~~'
};
function isSpace(char: string) {
  return /\s/.test(char);
}
function getWordBounds(value: string, pos: number) {
  let left = pos;
  let right = pos;
  while (left > 0 && !isSpace(value[left - 1] ?? ' ')) left -= 1;
  while (right < value.length && !isSpace(value[right] ?? ' ')) right += 1;
  return {
    left,
    right
  };
}
type DecorationPosition = 'inside' | 'outside' | 'none';
function getDecorationPosition(value: string, start: number, end: number, marker: string): DecorationPosition {
  const selected = value.slice(start, end);
  const inside = selected.length >= 2 * marker.length && selected.startsWith(marker) && selected.endsWith(marker);
  const outside = value.slice(start - marker.length, start) === marker && value.slice(end, end + marker.length) === marker;
  if (inside) return 'inside';
  if (outside) return 'outside';
  return 'none';
}
function applyPairAtCaret(ta: HTMLTextAreaElement, pos: number, marker: string) {
  const insert = marker + marker;
  setRangeText(ta, insert, {
    start: pos,
    end: pos,
    selectionMode: 'end'
  });
  const caretPos = pos + marker.length;
  ta.setSelectionRange(caretPos, caretPos);
  ta.focus();
}
type ToggleAction = 'add' | 'remove';
function getReplacementAndRange(value: string, start: number, end: number, marker: string) {
  const selected = value.slice(start, end);
  const position = getDecorationPosition(value, start, end, marker);
  if (position === 'inside') {
    return {
      rangeStart: start,
      rangeEnd: end,
      replacement: selected.slice(marker.length, selected.length - marker.length),
      action: 'remove' as ToggleAction,
      position: 'inside' as DecorationPosition
    };
  }
  if (position === 'outside') {
    return {
      rangeStart: start - marker.length,
      rangeEnd: end + marker.length,
      replacement: selected,
      action: 'remove' as ToggleAction,
      position: 'outside' as DecorationPosition
    };
  }
  return {
    rangeStart: start,
    rangeEnd: end,
    replacement: marker + selected + marker,
    action: 'add' as ToggleAction,
    position: 'none' as DecorationPosition
  };
}
function computeNewSelection(textarea: HTMLTextAreaElement, action: ToggleAction, markerLength: number, position: DecorationPosition, keepCaretPos: boolean) {
  let {
    selectionStart,
    selectionEnd
  } = textarea;
  function shift(delta: number) {
    selectionStart += delta;
    selectionEnd += delta;
  }
  if (action === 'add' && position === 'none') {
    shift(markerLength);
  }
  if (action === 'remove') {
    if (keepCaretPos || position === 'outside') {
      shift(-markerLength);
    } else if (position === 'inside') {
      selectionEnd -= markerLength * 2;
    }
  }
  return {
    selectionStart,
    selectionEnd
  };
}
function setRangeText(ta: HTMLTextAreaElement, replacement: string, options: SetRangeTextOptions = {}) {
  const {
    start = ta.selectionStart,
    end = ta.selectionEnd,
    selectionMode = 'preserve'
  } = options;
  ta.setRangeText(replacement, start, end, selectionMode);
  // Trigger input event to update the value
  ta.dispatchEvent(new InputEvent('input', {
    bubbles: true
  }));
}
function createSnapshot(ta: HTMLTextAreaElement): Snapshot {
  return {
    value: ta.value,
    selectionStart: ta.selectionStart,
    selectionEnd: ta.selectionEnd
  };
}
function snapshotsEqual(a?: Snapshot, b?: Snapshot) {
  if (!a || !b) return false;
  return a.value === b.value && a.selectionStart === b.selectionStart && a.selectionEnd === b.selectionEnd;
}
export function useCommentEditor(t0) {
  const $ = _c(28);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    onModEnter,
    onEscape
  } = options;
  const textareaRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const undoStackRef = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const redoStackRef = useRef(t3);
  const isComposingRef = useRef(false);
  const isApplyingHistoryRef = useRef(false);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = snap => {
      const stack = undoStackRef.current;
      const last = stack.at(-1);
      if (!snapshotsEqual(last, snap)) {
        stack.push(snap);
        if (stack.length > MAX_UNDO_STACK_SIZE) {
          stack.shift();
        }
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const pushUndo = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function clearRedo() {
      redoStackRef.current = [];
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const clearRedo = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function applySnapshot(ta, snap_0) {
      isApplyingHistoryRef.current = true;
      setRangeText(ta, snap_0.value, {
        start: 0,
        end: ta.value.length
      });
      ta.setSelectionRange(snap_0.selectionStart, snap_0.selectionEnd);
      ta.dispatchEvent(new InputEvent("input", {
        bubbles: true
      }));
      isApplyingHistoryRef.current = false;
      ta.focus();
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const applySnapshot = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const ta_0 = textareaRef.current;
      const stack_0 = undoStackRef.current;
      if (!ta_0 || stack_0.length <= 1) {
        return;
      }
      const current = stack_0.pop();
      if (!current) {
        return;
      }
      redoStackRef.current.push(current);
      const target = stack_0.at(-1);
      if (target) {
        applySnapshot(ta_0, target);
      }
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const undo = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const ta_1 = textareaRef.current;
      const next = redoStackRef.current.pop();
      if (!ta_1 || !next) {
        return;
      }
      pushUndo(next);
      applySnapshot(ta_1, next);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const redo = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = type => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      const marker = MARKER_MAP[type];
      const {
        value
      } = textarea;
      let {
        selectionStart: start,
        selectionEnd: end
      } = textarea;
      let keepCaretPos = false;
      if (start === end) {
        const {
          left,
          right
        } = getWordBounds(value, start);
        const noWordUnderCaret = left === right;
        if (noWordUnderCaret) {
          applyPairAtCaret(textarea, start, marker);
          return;
        }
        start = left;
        end = right;
        keepCaretPos = true;
      }
      const {
        rangeStart,
        rangeEnd,
        replacement,
        action,
        position
      } = getReplacementAndRange(value, start, end, marker);
      const {
        selectionStart,
        selectionEnd
      } = computeNewSelection(textarea, action, marker.length, position, keepCaretPos);
      setRangeText(textarea, replacement, {
        start: rangeStart,
        end: rangeEnd,
        selectionMode: "preserve"
      });
      textarea.setSelectionRange(selectionStart, selectionEnd);
      textarea.focus();
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const decorateText = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event => {
      const ta_2 = event.currentTarget;
      if (isApplyingHistoryRef.current) {
        return;
      }
      if (isComposingRef.current) {
        return;
      }
      pushUndo(createSnapshot(ta_2));
      clearRedo();
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const handleInput = t10;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_0 => {
      isComposingRef.current = true;
      pushUndo(createSnapshot(event_0.currentTarget));
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const handleCompositionStart = t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event_1 => {
      isComposingRef.current = false;
      pushUndo(createSnapshot(event_1.currentTarget));
      clearRedo();
    };
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const handleCompositionEnd = t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = function handleEmptyListItem(event_2, currentLine) {
      if (!textareaRef.current) {
        return;
      }
      const patterns = [/^\s*[-*+]\s\[[x\s]\]\s$/, /^\d+\.\s$/, /^\s*[-*+]\s$/];
      if (patterns.some(pattern => pattern.test(currentLine))) {
        event_2.preventDefault();
        const {
          selectionStart: selectionStart_0,
          value: value_0
        } = textareaRef.current;
        const lineStart = value_0.lastIndexOf("\n", selectionStart_0 - 1) + 1;
        const lineEnd = selectionStart_0;
        setRangeText(textareaRef.current, "", {
          start: lineStart,
          end: lineEnd,
          selectionMode: "start"
        });
        return true;
      }
      return false;
    };
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const handleEmptyListItem = t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event_3 => {
      if (event_3.metaKey || event_3.ctrlKey) {
        bb64: switch (event_3.key.toLowerCase()) {
          case "z":
            {
              event_3.preventDefault();
              if (event_3.shiftKey) {
                redo();
              } else {
                undo();
              }
              break bb64;
            }
          case "y":
            {
              event_3.preventDefault();
              redo();
              break bb64;
            }
          case "b":
            {
              event_3.preventDefault();
              decorateText("bold");
              break bb64;
            }
          case "i":
            {
              event_3.preventDefault();
              decorateText("italic");
              break bb64;
            }
          case "s":
            {
              event_3.preventDefault();
              decorateText("strikethrough");
              break bb64;
            }
          default:
        }
      }
    };
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const handleShortcut = t14;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (event_4, target_0) => {
      if (event_4.key !== "Tab") {
        return;
      }
      event_4.preventDefault();
      const {
        selectionStart: selectionStart_1,
        selectionEnd: selectionEnd_0,
        value: value_1
      } = target_0;
      pushUndo(createSnapshot(target_0));
      if (!value_1) {
        setRangeText(target_0, "  ", {
          selectionMode: "end"
        });
        clearRedo();
        return;
      }
      const lineStart_0 = value_1.lastIndexOf("\n", selectionStart_1 - 1) + 1;
      const endWithinIndex = Math.max(selectionStart_1, selectionEnd_0 - 1);
      const lastLineStart = value_1.lastIndexOf("\n", Math.max(0, endWithinIndex - 1)) + 1;
      const nextNewline = value_1.indexOf("\n", endWithinIndex);
      const lineBlockEnd = nextNewline === -1 ? value_1.length : nextNewline;
      const isMultiLineSelection = lastLineStart !== lineStart_0;
      if (isMultiLineSelection) {
        const block = value_1.slice(lineStart_0, lineBlockEnd);
        const lines = block.split("\n");
        if (event_4.shiftKey) {
          const transformed = lines.map(_temp).join("\n");
          setRangeText(target_0, transformed, {
            start: lineStart_0,
            end: lineBlockEnd,
            selectionMode: "preserve"
          });
        } else {
          const transformed_0 = lines.map(_temp2).join("\n");
          setRangeText(target_0, transformed_0, {
            start: lineStart_0,
            end: lineBlockEnd,
            selectionMode: "preserve"
          });
        }
        clearRedo();
        return;
      }
      if (event_4.shiftKey) {
        if (value_1.startsWith("  ", lineStart_0) || value_1[lineStart_0] === "\t") {
          const removeLen = value_1.startsWith("  ", lineStart_0) ? 2 : 1;
          setRangeText(target_0, "", {
            start: lineStart_0,
            end: lineStart_0 + removeLen,
            selectionMode: "preserve"
          });
          clearRedo();
        }
        return;
      }
      setRangeText(target_0, "  ", {
        selectionMode: "end"
      });
      clearRedo();
    };
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const handleTab = t15;
  let t16;
  if ($[16] !== onEscape) {
    t16 = event_5 => {
      if (event_5.key === "Escape") {
        event_5.preventDefault();
        onEscape?.();
        return true;
      }
      return false;
    };
    $[16] = onEscape;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const handleEscape = t16;
  let t17;
  if ($[18] !== onModEnter) {
    t17 = event_6 => {
      if (event_6.key === "Enter" && (event_6.metaKey || event_6.ctrlKey)) {
        event_6.preventDefault();
        onModEnter?.();
        return true;
      }
      return false;
    };
    $[18] = onModEnter;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  const handleModEnter = t17;
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (event_7, target_1) => {
      const {
        selectionStart: selectionStart_2,
        value: value_2
      } = target_1;
      if (event_7.key === "Enter" && !event_7.shiftKey && !event_7.metaKey && !event_7.ctrlKey) {
        const currentLine_0 = value_2.slice(0, Math.max(0, selectionStart_2)).split("\n").pop();
        if (!currentLine_0) {
          return;
        }
        if (handleEmptyListItem(event_7, currentLine_0)) {
          return;
        }
        const taskList = /^(\s*)([-*+])\s\[[x\s]\]\s/.exec(currentLine_0);
        if (taskList) {
          event_7.preventDefault();
          const text = `\n${taskList[1]}${taskList[2]} [ ] `;
          setRangeText(target_1, text, {
            selectionMode: "end"
          });
          return;
        }
        const orderedList = /^(\d+)\.\s/.exec(currentLine_0);
        if (orderedList?.[1]) {
          event_7.preventDefault();
          const number = Number.parseInt(orderedList[1], 10) + 1;
          const text_0 = `\n${number}. `;
          setRangeText(target_1, text_0, {
            selectionMode: "end"
          });
          return;
        }
        const unorderedList = /^(\s*)([-*+])\s/.exec(currentLine_0);
        if (unorderedList) {
          event_7.preventDefault();
          const text_1 = `\n${unorderedList[1]}${unorderedList[2]} `;
          setRangeText(target_1, text_1, {
            selectionMode: "end"
          });
        }
      }
    };
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  const handleListContinuation = t18;
  let t19;
  if ($[21] !== handleEscape || $[22] !== handleModEnter) {
    t19 = event_8 => {
      if (!textareaRef.current) {
        return;
      }
      handleShortcut(event_8);
      handleTab(event_8, textareaRef.current);
      if (handleEscape(event_8)) {
        return;
      }
      if (handleModEnter(event_8)) {
        return;
      }
      handleListContinuation(event_8, textareaRef.current);
    };
    $[21] = handleEscape;
    $[22] = handleModEnter;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const handleKeyDown = t19;
  let t20;
  let t21;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      const ta_3 = textareaRef.current;
      if (!ta_3) {
        return;
      }
      pushUndo(createSnapshot(ta_3));
    };
    t21 = [];
    $[24] = t20;
    $[25] = t21;
  } else {
    t20 = $[24];
    t21 = $[25];
  }
  useEffect(t20, t21);
  let t22;
  if ($[26] !== handleKeyDown) {
    t22 = {
      textareaRef,
      decorateText,
      undo,
      redo,
      handleKeyDown,
      handleInput,
      handleCompositionStart,
      handleCompositionEnd
    };
    $[26] = handleKeyDown;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  return t22;
}
function _temp2(line_0) {
  return "  " + line_0;
}
function _temp(line) {
  if (line.startsWith("  ")) {
    return line.slice(2);
  }
  if (line.startsWith("\t")) {
    return line.slice(1);
  }
  return line;
}